'use client'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/accordion'
import { Alert, AlertDescription, AlertTitle } from '@/components/alerts'
import { Button } from '@/components/ui/common/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import {
  AlertTriangle,
  Bell,
  ChevronRight,
  CircleHelp,
  CircleHelpIcon,
  FileText,
  Heart,
  PlayCircle
} from 'lucide-react'
import { useState } from 'react'

interface CognitiveAssessmentDialogProps {
  title?: string
  goal?: string
  limitations?: string
  procedure?: string
  recommendations?: string
  onAccept?: () => void
  isPreview?: boolean
}

export default function CognitiveAssessmentDialog({
  title = 'Narzędzie Oceny Poznawczej',
  goal = 'Ten test został zaprojektowany do pomiaru różnych zdolności poznawczych, w tym pamięci, uwagi, rozwiązywania problemów i szybkości przetwarzania. Wyniki mogą pomóc w identyfikacji mocnych stron poznawczych i obszarów wymagających poprawy, dostarczając cennych informacji dla rozwoju osobistego i monitorowania zdrowia poznawczego.',
  limitations = 'Treść ograniczeń testu poznawczego.',
  procedure = 'Opis procedury testowej.',
  recommendations = 'Zalecenia dotyczące stanu psychicznego przed przystąpieniem do testu.',
  onAccept,
  isPreview = false
}: CognitiveAssessmentDialogProps) {
  const [open, setOpen] = useState(false)
  const [expandedItems, setExpandedItems] = useState<string[]>(['item-1'])

  const handleAccept = () => {
    setOpen(false)
    onAccept?.()
  }

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        {!isPreview ? (
          <Button className="w-full bg-[#2bcaca] hover:bg-[#229999]">
            <PlayCircle className="mr-2 h-4 w-4" />
            Rozpocznij test
          </Button>
        ) : (
          <Button
            className="w-full border border-gray-200 transition-colors hover:border-gray-300"
            variant="outline"
          >
            Zobacz szczegóły testu
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto border border-gray-200 sm:max-w-[600px]">
        <DialogHeader className="flex flex-row items-center gap-3 pb-2">
          <div className="flex items-center justify-center rounded-full bg-[#2bcaca] p-2">
            <CircleHelpIcon className="h-5 w-5 text-white" />
          </div>
          <DialogTitle className="text-xl font-bold text-gray-900">{title}</DialogTitle>
        </DialogHeader>

        <DialogDescription className="text-sm text-gray-600">
          Witamy w Narzędziu Oceny Poznawczej. Prosimy o zapoznanie się z poniższymi
          informacjami przed kontynuowaniem.
        </DialogDescription>

        <div className="space-y-4 py-4">
          <Accordion
            className="w-full"
            onValueChange={setExpandedItems}
            type="multiple"
            value={isPreview ? ['item-1', 'item-2', 'item-3', 'item-4'] : expandedItems}
          >
            <AccordionItem
              className="rounded-md border border-gray-200 px-2"
              value="item-1"
            >
              <AccordionTrigger className="py-3 hover:no-underline">
                <div className="flex items-center gap-2">
                  <CircleHelp className="h-5 w-5 text-emerald-500" />
                  <span className="font-medium text-gray-900">Cel Testu</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-2 pb-4 text-sm text-gray-600">
                {goal}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              className="mt-2 rounded-md border border-gray-200 px-2"
              value="item-2"
            >
              <AccordionTrigger className="py-3 hover:no-underline">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  <span className="font-medium text-gray-900">Ograniczenia</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-2 pb-4 text-sm text-gray-600">
                {limitations}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              className="mt-2 rounded-md border border-gray-200 px-2"
              value="item-3"
            >
              <AccordionTrigger className="py-3 hover:no-underline">
                <div className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-rose-500" />
                  <span className="font-medium text-gray-900">Procedura Testowa</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-2 pb-4 text-sm text-gray-600">
                {procedure}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              className="mt-2 rounded-md border border-gray-200 px-2"
              value="item-4"
            >
              <AccordionTrigger className="py-3 hover:no-underline">
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-violet-500" />
                  <span className="font-medium text-gray-900">
                    Zalecenia Dotyczące Stanu Psychicznego
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-2 pb-4 text-sm text-gray-600">
                {recommendations}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {!isPreview && (
            <Alert className="border-[#2bcaca]/20 bg-[#2bcaca]/5" variant="default">
              <FileText className="h-5 w-5 text-[#2bcaca]" />
              <AlertTitle className="text-gray-900">
                Warunki Przetwarzania Danych
              </AlertTitle>
              <AlertDescription className="text-sm text-gray-600">
                Twoje dane będą przetwarzane zgodnie z naszą{' '}
                <a className="text-[#2bcaca] underline hover:text-[#229999]" href="#">
                  polityką prywatności
                </a>
                . Kliknij poniżej, aby zobaczyć pełny tekst zgodny z RODO.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {!isPreview && (
          <DialogFooter className="flex justify-between gap-2 sm:justify-between">
            <Button
              className="text-[#2bcaca] hover:bg-[#2bcaca]/5 hover:text-[#229999]"
              variant="outline"
            >
              Zobacz Politykę Danych
            </Button>
            <Button
              className="bg-[#2bcaca] text-white hover:bg-[#229999]"
              onClick={handleAccept}
            >
              Rozumiem i Akceptuję
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
